import { ContainerRef, APIOutput } from "@amzn/ask-legal-domain";
import {
    Box,
    Button,
    Flashbar,
    FlashbarProps,
    Modal,
    SpaceBetween,
    StatusIndicator,
    Table,
    TableProps
} from "@amzn/awsui-components-react";
import * as React from "react";
import { AppContext } from "../../../setup/context";
import { NewEmployeePreview } from "../../../api/legal-contact.api";

interface SyncContactsModalProps {
    containerRef: ContainerRef;
    instanceId: string;
    onDismiss: () => void;
    /** Fires for any completed sync (including all-skipped), for effects that reflect "a sync ran" — e.g. the "Last synced" timestamp. */
    onSyncCompleted: () => void;
    /** Fires only when at least one contact was actually created, for effects that need new data — e.g. reloading the contact list. */
    onContactsAdded: () => void;
}

export const SyncContactsModal = (props: SyncContactsModalProps) => {
    const context = React.useContext(AppContext);
    const [loading, setLoading] = React.useState(false);
    const [syncing, setSyncing] = React.useState(false);
    const [newEmployees, setNewEmployees] = React.useState<NewEmployeePreview[]>([]);
    const [selectedItems, setSelectedItems] = React.useState<NewEmployeePreview[]>([]);
    const [flashbarItems, setFlashbarItems] = React.useState<FlashbarProps.MessageDefinition[]>([]);

    const columnDefinitions: TableProps.ColumnDefinition<NewEmployeePreview>[] = [
        {
            id: "name",
            header: "Name",
            cell: (item) => item.name,
            sortingField: "name"
        },
        {
            id: "login",
            header: "Login",
            cell: (item) => item.login,
            sortingField: "login"
        },
        {
            id: "email",
            header: "Email",
            cell: (item) => item.email
        },
        {
            id: "manager",
            header: "Manager",
            cell: (item) => `${item.managerName} (${item.managerLogin})`
        }
    ];

    const loadPreview = React.useCallback(async () => {
        setLoading(true);
        setFlashbarItems([]);
        try {
            const response = await context.getLegalContactAPI().wml.previewSync({
                containerId: props.containerRef.id,
                instanceId: props.instanceId
            });

            const output = APIOutput.fromRaw<any>(response.data);
            if (output.isErr()) {
                throw new Error(output.err.message);
            }

            const result = output.data;
            setNewEmployees(result.newEmployees || []);
            if ((result.count || 0) === 0) {
                setFlashbarItems([{
                    type: "info",
                    content: "No new contacts to add. All eligible employees are already listed.",
                    dismissible: true
                }]);
            }
        } catch (error) {
            const err = error as Error;
            setFlashbarItems([{
                type: "error",
                header: "Failed to load preview",
                content: err.message,
                dismissible: true
            }]);
        } finally {
            setLoading(false);
        }
    }, [props.containerRef.id, props.instanceId, context]);

    const syncSelected = async () => {
        if (selectedItems.length === 0) return;

        setSyncing(true);
        setFlashbarItems([]);

        try {
            const response = await context.getLegalContactAPI().wml.syncContainer({
                containerId: props.containerRef.id,
                employeeManagerPairs: selectedItems.map(emp => ({
                    employeeLogin: emp.login,
                    managerLogin: emp.managerLogin
                }))
            });

            const output = APIOutput.fromRaw<any>(response.data);
            if (output.isErr()) {
                throw new Error(output.err.message);
            }

            const result = output.data;
            const contactsAdded = result.contactsAdded || 0;
            const contactsFailed = result.contactsFailed || 0;
            const contactsSkipped = result.contactsSkipped || 0;
            const failures = result.failures || [];
            const skipped: Array<{ login: string; reason?: string }> = result.skipped || [];

            // Skips can happen for two distinct reasons (server `reason` codes): the employee is
            // confirmed inactive, or PAPI has no record for the login. Render each entry's own
            // reason so the admin isn't told "inactive" for an unresolvable login.
            const skipReasonLabel = (reason?: string): string => {
                switch (reason) {
                    case "INACTIVE_EMPLOYEE": return "inactive";
                    case "UNRESOLVED_EMPLOYEE": return "not found in PAPI";
                    default: return "skipped";
                }
            };
            const skippedDetail = skipped.length > 0
                ? ` ${contactsSkipped} skipped (${skipped.map(s => `${s.login}: ${skipReasonLabel(s.reason)}`).join(", ")}).`
                : "";

            if (contactsFailed === 0 && contactsSkipped === 0) {
                // Everything selected was added.
                setFlashbarItems([{
                    type: "success",
                    header: `Successfully added ${contactsAdded} contact${contactsAdded !== 1 ? "s" : ""}`,
                    dismissible: true
                }]);
                // Completed sync with new contacts: refresh both the "last synced" stamp and the list.
                props.onSyncCompleted();
                props.onContactsAdded();
                setTimeout(() => {
                    props.onDismiss();
                }, 1500);
            } else if (contactsAdded === 0 && contactsFailed > 0) {
                // All failed - show as error (red). (Defensive: today add() returns an error output
                // on any failure, so this path is reached via the isErr() branch above; kept for safety.)
                const failureDetails = failures.map(f => `${f.login}: ${f.error}`).join(", ");
                setFlashbarItems([{
                    type: "error",
                    header: `Failed to add ${contactsFailed} contact${contactsFailed !== 1 ? "s" : ""}`,
                    content: (failureDetails || "All contacts failed to add.") + skippedDetail,
                    dismissible: true
                }]);
            } else {
                // Partial outcome (some added and/or some skipped) - warning (yellow).
                // Reconcile against the full selected set so skipped contacts are never silently dropped.
                const failureDetails = failures.length > 0
                    ? ` ${contactsFailed} failed (${failures.map(f => `${f.login}: ${f.error}`).join(", ")}).`
                    : "";
                setFlashbarItems([{
                    type: "warning",
                    header: `Added ${contactsAdded} of ${selectedItems.length} contact${selectedItems.length !== 1 ? "s" : ""}`,
                    content: `${failureDetails}${skippedDetail}`.trim() || "Some selected contacts were not added.",
                    dismissible: true
                }]);
                // The sync completed (even if everyone was skipped), so refresh the "last synced"
                // stamp. Only reload the contact list when something was actually added — an
                // all-skipped / zero-added sync has nothing new to show. Never auto-dismiss here;
                // the admin needs to see what was skipped/failed.
                props.onSyncCompleted();
                if (contactsAdded > 0) {
                    props.onContactsAdded();
                }
            }
        } catch (error) {
            const err = error as Error;
            const errorMessage = err.message || "An unexpected error occurred";
            setFlashbarItems([{
                type: "error",
                header: "Failed to sync contacts",
                content: errorMessage,
                dismissible: true
            }]);
        } finally {
            setSyncing(false);
        }
    };

    React.useEffect(() => {
        loadPreview();
        setSelectedItems([]);
    }, [loadPreview]);

    return (
        <Modal
            visible={true}
            onDismiss={syncing ? undefined : props.onDismiss}
            header="Sync Contacts"
            size="large"
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" onClick={props.onDismiss} disabled={syncing}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={syncSelected}
                            disabled={selectedItems.length === 0 || syncing}
                            loading={syncing}
                        >
                            Add Selected ({selectedItems.length})
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween size="m">
                {flashbarItems.length > 0 && (
                    <Flashbar items={flashbarItems} />
                )}

                {loading ? (
                    <Box textAlign="center" padding="xxl">
                        <StatusIndicator type="loading">
                            Checking for new contacts...
                        </StatusIndicator>
                    </Box>
                ) : newEmployees.length === 0 ? (
                    <Box textAlign="center" padding="xxl" color="text-body-secondary">
                        No new contacts to add.
                    </Box>
                ) : (
                    <>
                        <Box variant="p">
                            <SpaceBetween size="xs">
                                <Box>
                                    <strong>{newEmployees.length}</strong> new employee{newEmployees.length !== 1 ? "s" : ""} found
                                </Box>
                                <Box color="text-body-secondary" fontSize="body-s">
                                    Selected employees will be added as "New Contact" with settings inherited from their manager.
                                    Each employee will receive an email notification to review and update their contact information.
                                </Box>
                            </SpaceBetween>
                        </Box>
                        <Table
                            columnDefinitions={columnDefinitions}
                            items={newEmployees}
                            loading={loading || syncing}
                            loadingText={syncing ? "Adding contacts..." : "Loading contacts"}
                            selectionType="multi"
                            selectedItems={selectedItems}
                            onSelectionChange={({ detail }) =>
                                setSelectedItems(detail.selectedItems)
                            }
                            empty={
                                <Box textAlign="center" color="inherit">
                                    No employees found
                                </Box>
                            }
                            header={
                                <SpaceBetween size="s" direction="horizontal">
                                    <Button
                                        variant="normal"
                                        iconName="check"
                                        onClick={() => setSelectedItems(newEmployees)}
                                        disabled={syncing || selectedItems.length === newEmployees.length}
                                    >
                                        Select all
                                    </Button>
                                    <Button
                                        variant="normal"
                                        iconName="close"
                                        onClick={() => setSelectedItems([])}
                                        disabled={syncing || selectedItems.length === 0}
                                    >
                                        Clear selection
                                    </Button>
                                </SpaceBetween>
                            }
                        />
                    </>
                )}
            </SpaceBetween>
        </Modal>
    );
};
